import React, { useRef, useState, useEffect } from 'react';
import styled from 'styled-components';
import throttle from 'lodash/throttle';
import Debounce from '@util/Debounce';

import { Section, Container } from '@components/global';
import MDXRenderer from '@article/Mdx';
import Hero from '@article/Hero/hero';
import Aside from '@article/Aside/Aside';
import Progress from '@article/Aside/Progress';

const Post = ({ post, coverImage, hideProgress }) => {
  const { body } = post;

  const contentSectionRef = useRef(null);
  const [hasCalculated, setHasCalculated] = useState(false);
  const [contentHeight, setContentHeight] = useState(0);

  useEffect(() => {
    const calculateBodySize = throttle(() => {
      const contentSection = contentSectionRef.current;

      if (!contentSection) return;

      /**
       * If we haven't checked the content's height before,
       * we want to add listeners to the content area's
       * imagery to recheck when it's loaded
       */
      if (!hasCalculated) {
        const debouncedCalculation = Debounce(calculateBodySize);
        const $imgs = contentSection.querySelectorAll('img');

        $imgs.forEach(($img) => {
          // If the image hasn't finished loading then add a listener
          if (!$img.complete) $img.onload = debouncedCalculation;
        });

        // Prevent rerun of the listener attachment
        setHasCalculated(true);
      }

      // Set the height and offset of the content area
      setContentHeight(contentSection.getBoundingClientRect().height);
    }, 20);

    calculateBodySize();
    window.addEventListener('resize', calculateBodySize);

    return () => window.removeEventListener('resize', calculateBodySize);
  }, [hasCalculated]);

  return (
    <Section>
      <Container>
        {!hideProgress && (
          <Aside contentHeight={contentHeight}>
            {!hideProgress && <Progress contentHeight={contentHeight} />}
          </Aside>
        )}
        <MDWrapper>
          <Hero post={post} coverImage={coverImage} />
          <ArticleBody heroHasCoverImage={coverImage} ref={contentSectionRef}>
            <MDXRenderer content={body} />
          </ArticleBody>
        </MDWrapper>
      </Container>
    </Section>
  );
};

const MDWrapper = styled.div`
  @media (max-width: ${(props) => props.theme.screen.md}) {
    padding-right 0;
  }
`;

const ArticleBody = styled.article`
  padding: ${(p) => (p.heroHasCoverImage ? '100' : '0')}px 0;
`;

export default Post;
